/* Copyright (c) 2020-present CloudIO Inc */

import { useIORef } from 'designer/useIORef';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { IO_OVERLAY } from 'utils/OverlayPortal';
import type { ResizeObserver } from './useComponentSize.type';

declare global {
  interface Window {
    ResizeObserver: typeof ResizeObserver;
  }
}

function getSize(el: HTMLElement | null): string {
  const array: [number, number][] = [];
  if (!el) {
    return JSON.stringify(array);
  }
  const parent = el.parentElement;
  if (parent) {
    for (let i = 0; i < parent.children.length; i++) {
      const child = parent.children.item(i) as HTMLElement;
      if (child && child.className !== IO_OVERLAY) {
        array.push([child.offsetWidth, child.offsetHeight]);
      }
    }
  }
  return JSON.stringify(array);
}

function observeSiblings(el: HTMLElement, observer: ResizeObserver) {
  const parent = el.parentElement;
  if (parent) {
    for (let i = 0; i < parent.children.length; i++) {
      const child = parent.children.item(i) as HTMLElement;
      if (child && child.className !== IO_OVERLAY) {
        observer.observe(child);
      }
    }
  }
}

const INITIAL_SIZE = '[]';

export default function useSiblingComponentSize(): {
  size: string;
  ref: (el: HTMLElement | null) => void;
  el: HTMLElement | null;
} {
  const [size, setSize] = useState(INITIAL_SIZE);
  const ioRef = useIORef<HTMLElement>(null);
  const elRef = useRef<HTMLElement | null>(null);

  const handleResize = useCallback(() => {
    setSize(getSize(elRef.current));
  }, []);

  useLayoutEffect(() => {
    return ioRef.subscribe((_el) => {
      if (elRef.current !== _el) {
        elRef.current = _el;
        handleResize();
      }
    });
  }, [handleResize, ioRef]);

  const el = elRef.current;

  useLayoutEffect(() => {
    if (!el) {
      return;
    }

    handleResize();
    if (typeof window.ResizeObserver === 'function') {
      // const frames: number[] = [];
      const resizeObserver = new window.ResizeObserver(() => {
        // resizeObserver.disconnect();
        handleResize();
        // frames.push(
        //   requestAnimationFrame(() => observeSiblings(current, resizeObserver)),
        // );
      });
      observeSiblings(el, resizeObserver);

      return () => {
        // frames.forEach(cancelAnimationFrame);
        resizeObserver.disconnect();
      };
    }
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [el, handleResize]);

  return { size, ref: ioRef, el };
}
